import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  enumEquals,
  UserRoleSchema,
  UserServiceCreateResponseJson,
} from '@vmspdb/definitions';
import { SelectOption, UserEmployeeRoles } from '@vmspdb/ui/domain';
import { selectAuthUserId } from '@vmspdb/ui/store';
import * as fromUsers from './users.reducer';

export const selectUserState = createFeatureSelector<fromUsers.UserState>(
  fromUsers.usersFeatureKey,
);

export const selectAllUsers = createSelector(
  selectUserState,
  fromUsers.selectAll,
);

export const selectAllEmployees = createSelector(
  selectAllUsers,
  selectAuthUserId,
  (users, currentUserId) =>
    users.filter(
      (item) =>
        item.uuid === currentUserId ||
        UserEmployeeRoles.some((role) =>
          enumEquals(UserRoleSchema, role, item.role || ''),
        ),
    ),
);

export const selectAllActiveEmployees = createSelector(
  selectAllUsers,
  selectAuthUserId,
  (users, currentUserId) => {
    return users.filter(
      (item) =>
        (item.active || item.uuid === currentUserId) &&
        UserEmployeeRoles.some((role) =>
          enumEquals(UserRoleSchema, role, item.role || ''),
        ),
    );
  },
);

export const selectAllEmployeeOptions = createSelector(
  selectAllEmployees,
  (state): SelectOption[] => {
    const values = state.sort((a, b) =>
      (a.name || '') < (b.name || '') ? -1 : 1,
    );
    return values
      .filter((item) => item.uuid)
      .map((item) => ({
        value: item.uuid || '',
        label: `${item.name}` + (item.active ? '' : ' (Inactive)'),
      }));
  },
);

export const selectAllEmployeeOptionsWithSelected = (
  selected: string | string[],
) =>
  createSelector(
    selectAllEmployees,
    selectAuthUserId,
    (state, currentUserId): SelectOption[] => {
      const values = state.sort((a, b) =>
        (a.name || '') < (b.name || '') ? -1 : 1,
      );
      const selectedValue = Array.isArray(selected) ? selected : [selected];
      return values
        .filter(
          (item) =>
            item.active ||
            item.uuid === currentUserId ||
            selectedValue.includes(item.uuid || ''),
        )
        .filter((item) => item.uuid)
        .map((item) => ({
          value: item.uuid || '',
          label: `${item.name}` + (item.active ? '' : ' (Inactive)'),
        }));
    },
  );

export const selectAllActiveEmployeeOptions = createSelector(
  selectAllActiveEmployees,
  (state): SelectOption[] => {
    const values = state.sort((a, b) =>
      (a.name || '') < (b.name || '') ? -1 : 1,
    );
    return values
      .filter((item) => item.uuid)
      .map((item) => ({
        value: item.uuid || '',
        label: `${item.name}`,
      }));
  },
);

export const selectAllEmployeeOptionsWithAll = createSelector(
  selectAllEmployeeOptions,
  (state): SelectOption[] => {
    return [
      {
        label: 'All Employees',
        value: '',
      },
      ...state,
    ];
  },
);

export const selectAllActiveEmployeeOptionsWithAll = createSelector(
  selectAllActiveEmployeeOptions,
  (state): SelectOption[] => {
    return [
      {
        label: 'All Employees',
        value: '',
      },
      ...state,
    ];
  },
);

export const selectAllEmployeesMapped = createSelector(
  selectAllEmployees,
  (employees) => {
    const map = new Map<string, UserServiceCreateResponseJson>();
    employees.forEach((employee) => {
      if (employee.uuid) {
        map.set(employee.uuid, employee);
      }
    });
    return map;
  },
);
